import { useQuery } from '@apollo/client'
import { Autocomplete, TextField } from '@mui/material'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { getFullName } from 'shared/utils'
import { GetRecruitersByName } from './RecruitersAutocomplete.gql'
import { RecruitersAutocompleteProps } from './RecruitersAutocompleteProps'

const LIMIT = 25
/**
 * multiple + single autocompletes are separated to workaround MUI typing
 * @param RecruitersAutocompleteProps
 * @returns MUI Autocomplete with paginated options (multi-select)
 */
const RecruitersAutocompleteMultiple: FC<RecruitersAutocompleteProps<true>> = ({
    error,
    helperText,
    size,
    placeholder,
    label = 'Recruiters',
    renderOption: customRenderOption,
    ...props
}) => {
    const [name, setName] = useState('')
    const [debounceName, setDebouncedName] = useState(name)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedName(name)
            nextPageToFetch.current = 2
        }, 500)

        return () => {
            clearTimeout(handler)
        }
    }, [name])

    const { data, fetchMore, loading } = useQuery(GetRecruitersByName, {
        variables: { name: debounceName, page: 1, limit: LIMIT },
        fetchPolicy: 'network-only',
        skip: !open,
    })

    const loadMoreData = useCallback(() => {
        fetchMore({
            variables: {
                page: nextPageToFetch.current,
                limit: LIMIT,
                name: debounceName,
            },
            updateQuery(
                previousData,
                { fetchMoreResult, variables: { page, limit } }
            ) {
                if (
                    !previousData?.recruiters?.data ||
                    !fetchMoreResult?.recruiters?.data
                )
                    return fetchMoreResult ?? previousData
                const updatedCompaniesData = Array.from(
                    previousData.recruiters.data
                )
                const pageLimit = previousData.recruiters.pageInfo.pageTotal
                nextPageToFetch.current =
                    page + 1 <= pageLimit ? page + 1 : page
                for (
                    let i = 0;
                    i < fetchMoreResult.recruiters.data.length;
                    i++
                ) {
                    updatedCompaniesData[(page - 1) * limit + i] =
                        fetchMoreResult.recruiters.data[i]
                }
                const newCache = {
                    ...previousData,
                    recruiters: {
                        ...fetchMoreResult.recruiters,
                        data: updatedCompaniesData,
                    },
                }
                if (
                    fetchMoreResult?.recruiters?.pageInfo.itemTotal &&
                    fetchMoreResult.recruiters.pageInfo.itemTotal ===
                        updatedCompaniesData.length
                ) {
                    observer.current?.disconnect()
                }
                return newCache
            },
        })
    }, [fetchMore, debounceName])

    const observerCallback = useCallback(
        (entries) => {
            if (entries[0].isIntersecting) {
                loadMoreData()
            }
        },
        [loadMoreData]
    )

    const observedElementRef = useRef<HTMLLIElement | null>(null)
    const nextPageToFetch = useRef<number>(2)
    const observer = useRef<IntersectionObserver>(
        new IntersectionObserver(observerCallback)
    )

    useEffect(() => {
        observer.current = new IntersectionObserver(observerCallback)
        // update which element we are observing as data changes
        if (observedElementRef.current) {
            observer.current?.observe(observedElementRef.current)
        }

        return () => {
            if (observer.current) observer.current.disconnect()
        }
        // open is a dependency because we lose the observedElementRef when the dropdown closes and need to re-observe it once we open again
    }, [observerCallback, open, data?.recruiters.data])

    return (
        <Autocomplete
            fullWidth
            multiple
            getOptionLabel={(option) =>
                option?.user ? getFullName(option?.user) : ''
            }
            filterOptions={(x) => x}
            disablePortal
            onOpen={() => setOpen(true)}
            onClose={(event, reason) => {
                if (reason === 'blur') {
                    setOpen(false) // Close only on blur
                }
            }}
            loading={loading}
            open={open}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(liProps, option, state, ownerState) => {
                const isObserver = state.index === ownerState.options.length - 1

                if (customRenderOption) {
                    const element = customRenderOption(
                        liProps,
                        option,
                        state,
                        ownerState
                    )
                    if (isObserver && React.isValidElement(element)) {
                        return React.cloneElement(element, {
                            ref: observedElementRef,
                        } as React.Attributes & {
                            ref: typeof observedElementRef
                        })
                    }
                    return element
                }

                return (
                    <li
                        {...liProps}
                        key={option.id}
                        ref={isObserver ? observedElementRef : null}
                    >
                        {option?.user ? getFullName(option?.user) : ''}
                    </li>
                )
            }}
            onInputChange={(e, value) => {
                setName(value)
            }}
            options={data?.recruiters.data || []}
            renderInput={(params) => (
                <TextField
                    {...params}
                    size={size}
                    label={label}
                    variant="outlined"
                    error={error}
                    helperText={helperText}
                    placeholder={placeholder}
                />
            )}
            {...props}
        />
    )
}

export default RecruitersAutocompleteMultiple
